import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import socketService from '../services/socketService';
import { useAuth } from '../state/auth/AuthContext';

const SocketContext = createContext(null);

export const useSocketContext = () => {
  const context = useContext(SocketContext);
  if (!context) {
    // Return default values instead of throwing error during initial render
    return {
      isConnected: false,
      socketId: null,
      error: null,
      subscribeToQueue: () => console.warn('Socket not initialized'),
      unsubscribeFromQueue: () => console.warn('Socket not initialized'),
      addEventListener: () => console.warn('Socket not initialized'),
      removeEventListener: () => console.warn('Socket not initialized'),
      emitEvent: () => console.warn('Socket not initialized'),
      socket: null,
    };
  }
  return context;
};

export const SocketProvider = ({ children }) => {
  const authContext = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const [socketId, setSocketId] = useState(null);
  const [error, setError] = useState(null);

  // Get user and isAuthenticated safely
  const user = authContext?.user || null;
  const isAuthenticated = authContext?.isAuthenticated || false;
  const accessToken = authContext?.accessToken || null;

  // Initialize socket connection when user is authenticated
  useEffect(() => {
    if (isAuthenticated && user && accessToken) {
      const handleConnectionSuccess = ({ socketId }) => {
        setIsConnected(true);
        setSocketId(socketId);
        setError(null);
      };

      const handleConnectionDisconnected = () => {
        setIsConnected(false);
        setSocketId(null);
      };

      const handleConnectionError = ({ error }) => {
        const errorMsg = error?.message || 'Connection error';
        setError(errorMsg);
      };

      const handleAuthFailed = () => {
        setError('Authentication failed');
        setIsConnected(false);
      };

      socketService.on('connection:success', handleConnectionSuccess);
      socketService.on('connection:disconnected', handleConnectionDisconnected);
      socketService.on('connection:error', handleConnectionError);
      socketService.on('auth:failed', handleAuthFailed);
      
      const socket = socketService.connect();

      return () => {
        socketService.off('connection:success', handleConnectionSuccess);
        socketService.off('connection:disconnected', handleConnectionDisconnected);
        socketService.off('connection:error', handleConnectionError);
        socketService.off('auth:failed', handleAuthFailed);
        socketService.disconnect();
      }
    } else {
      if (socketService.socket) {
        socketService.disconnect();
        setIsConnected(false);
        setSocketId(null);
      }
    }
  }, [isAuthenticated, user, accessToken]);

  const subscribeToQueue = useCallback((doctorId) => {
    if (!isConnected) {
      console.warn('Cannot subscribe to queue: Socket not connected');
      return;
    }
    socketService.subscribeToQueue(doctorId);
  }, [isConnected]);

  const unsubscribeFromQueue = useCallback((doctorId) => {
    if (!isConnected) {
      console.warn('Cannot unsubscribe from queue: Socket not connected');
      return;
    }
    socketService.unsubscribeFromQueue(doctorId);
  }, [isConnected]);

  const addEventListener = useCallback((event, handler) => {
    socketService.on(event, handler);
  }, []);

  const removeEventListener = useCallback((event, handler) => {
    socketService.off(event, handler);
  }, []);

  const emitEvent = useCallback((event, data) => {
    socketService.emit(event, data);
  }, []);

  const value = {
    isConnected,
    socketId,
    error,
    subscribeToQueue,
    unsubscribeFromQueue,
    addEventListener,
    removeEventListener,
    emitEvent,
    socket: socketService.socket,
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};

export default SocketContext;
